// TablaEmpresaLista.js

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import IconosGenerales from "../../elementos/iconos/IconosGenerales";
import { visuallyHidden } from '@mui/utils';
import { styled } from '@mui/material/styles';
import PaginacionTabla from '../eventos/filtros/SelectPaginaTabla';
import SelectNumeroCursos from '../eventos/filtros/SelectNumeroElementos';
import SelectDinamicoLista from '../eventos/filtros/SelectDinamicoLista';
import Buscador from '../eventos/filtros/BuscadorDinamico';
import { useNavigate, useLocation } from 'react-router-dom';
import CustomTooltip from './../eventos/avisos/Tooltip';
import BotonesAccion from "../../elementos/eventos/navegacion_y_accion/BotonesAccion";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    textAlign: 'center  !important',
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: 'rgba(0, 60, 102, 0.8)',
        color: theme.palette.common.white,
        padding: '0px',
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    textAlign: 'center  !important',
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

const WhiteTableSortLabel = styled(TableSortLabel)(({ theme }) => ({
    textAlign: 'center  !important',
    color: `${theme.palette.common.white} !important`,
    '&.Mui-active .MuiTableSortLabel-icon': {
        color: `${theme.palette.common.white} !important`,
    },
}));

function descendingComparator(a, b, orderBy, personas) {
    if (orderBy === 'departamentos') {
        if (b.Departamentos.length < a.Departamentos.length) {
            return -1;
        }
        if (b.Departamentos.length > a.Departamentos.length) {
            return 1;
        }
    }
    if (orderBy === 'personas') {
        const personasA = personas.filter(persona => persona.Cargos.some(cargo => cargo.empresa_id === a.Id)).length;
        const personasB = personas.filter(persona => persona.Cargos.some(cargo => cargo.empresa_id === b.Id)).length;
        return personasB - personasA;
    }
    return b[orderBy] < a[orderBy] ? -1 : b[orderBy] > a[orderBy] ? 1 : 0;
}

function getComparator(order, orderBy, personas) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy, personas)
        : (a, b) => -descendingComparator(a, b, orderBy, personas);
}

function stableSort(array, comparator) {
    const arrayCopy = [...array];
    if (comparator) arrayCopy.sort(comparator);
    return arrayCopy;
}

function EnhancedTableHead(props) {
    const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort, header } = props;
    const createSortHandler = (property) => (event) => onRequestSort(event, property);

    return (
        <TableHead>
            <StyledTableRow>
                <StyledTableCell padding="checkbox">
                    <Checkbox
                        style={{ color: 'white' }}
                        indeterminate={numSelected > 0 && numSelected < rowCount}
                        checked={rowCount > 0 && numSelected === rowCount}
                        onChange={onSelectAllClick}
                        inputProps={{ 'aria-label': 'select all' }}
                    />
                </StyledTableCell>
                {header.map((headCell) => (
                    <StyledTableCell
                        key={headCell.id}
                        align={headCell.numeric ? 'right' : 'left'}
                        padding={headCell.disablePadding ? 'none' : 'normal'}
                        sortDirection={orderBy === headCell.id ? order : false}
                    >
                        <WhiteTableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id)}
                        >
                            {headCell.label}
                            {orderBy === headCell.id && (
                                <Box component="span" sx={visuallyHidden}>
                                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                </Box>
                            )}
                        </WhiteTableSortLabel>
                    </StyledTableCell>
                ))}
            </StyledTableRow>
        </TableHead>
    );
}

function EnhancedTableToolbar({ numSelected, departamentos, setEliminarSeleccion, tipoColaboradora, tipoEmpresa }) {
    const [estadoBusqueda, setEstadoBusqueda] = useState(false);
    const [escribiendo, setEscribiendo] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const busquedaParam = searchParams.get('busqueda') || '';

    const toggleSeleccion = (valor) => {
        setEliminarSeleccion((prevState) => ({ ...prevState, valor }));
    };

    const actualizarBusqueda = (valor) => {
        const params = new URLSearchParams(location.search);
        if (valor.trim()) {
            params.set('busqueda', valor);
        } else {
            params.delete('busqueda');
        }
        navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    };

    return (
        <Toolbar sx={{ pl: { sm: 2 }, pr: { xs: 1, sm: 1 }, ...(numSelected > 0 && {
            bgcolor: (theme) => alpha(theme.palette.common.white, theme.palette.action.activatedOpacity),
        }) }}>
            {numSelected > 0 ? (
                <Typography sx={{ flex: '1 1 100%' }} color="inherit" variant="subtitle1">
                    {numSelected} seleccionado(s)
                </Typography>
            ) : (
                <Typography sx={{ flex: '1 1 40%' }} variant="h6">
                    <BotonesAccion texto={tipoColaboradora ? "Crear Empresa Colaboradora" : tipoEmpresa ? "Crear Empresa": "Crear Negocio"} to={tipoColaboradora ? "/gestionpersonas/empresas-colaboradoras/crear" : tipoEmpresa ? "/gestionpersonas/empresas/crearEmpresa": "/gestionpersonas/negocios/crearNegocio"} />
                </Typography>
            )}

            {numSelected > 0 ? (
                <CustomTooltip mensaje="Eliminar select">
                    <div onClick={() => toggleSeleccion(false)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', width: '40px', height: '40px' }}>
                        <IconosGenerales papeleraT="true" id="eliminarSelect" style={{ width: '40px', height: '40px' }} />
                    </div>
                </CustomTooltip>
            ) : (
                <>
                    { !tipoEmpresa &&(
                        <>
                            <SelectDinamicoLista opciones={departamentos.filter(dep => dep.tipo === "interno").sort((a, b) => a.nombre.localeCompare(b.nombre))} parametroURL="departamento" etiqueta="Interno" />
                            <SelectDinamicoLista opciones={departamentos.filter(dep => dep.tipo === "organigrama").sort((a, b) => a.nombre.localeCompare(b.nombre))} parametroURL="departamento" etiqueta="Organigrama" />
                        </>
                    )}

                    <Buscador
                        placeholder={!tipoColaboradora && !tipoEmpresa ? "Buscar Negocio" : "Buscar Empresa"}  
                        tooltip="Ej: Nombre"
                        palabra={busquedaParam}
                        estadoBusqueda={estadoBusqueda}
                        setEstadoBusqueda={setEstadoBusqueda}
                        escribiendo={escribiendo}
                        setEscribiendo={setEscribiendo}
                        onBuscar={actualizarBusqueda}
                    />
                    <BotonesAccion icono="delete" to="/gestionpersonas/empresas" />
                </>
            )}
        </Toolbar>
    );
}

function EnhancedTable(props) {
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);

    const {
        data, header, meta, departamentos, personas,
        selected, setSelected, eliminarSeleccion, setEliminarSeleccion,
        tipoColaboradora, tipoEmpresa
    } = props;

    const order = searchParams.get('order') || 'asc';
    const orderBy = searchParams.get('orderBy') || 'nombre';

    const handleRequestSort = (event, property) => {
        const normalizedProperty = property.toLowerCase();
        const isAsc = orderBy === normalizedProperty && order === 'asc';
        const newOrder = isAsc ? 'desc' : 'asc';

        const params = new URLSearchParams(location.search);
        params.set('order', newOrder);
        params.set('orderBy', normalizedProperty);
        navigate({ pathname: location.pathname, search: params.toString() });
    };

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelected = data.map((n) => n.Id);
            setSelected(newSelected);
        } else {
            setSelected([]);
        }
    };

    const handleClick = (event, id) => {
        const selectedIndex = selected.indexOf(id);
        let newSelected = [...selected];
        if (selectedIndex === -1) {
            newSelected.push(id);
        } else {
            newSelected.splice(selectedIndex, 1);
        }
        setSelected(newSelected);
    };

    const isSelected = (id) => selected.includes(id);
    const sortedData = stableSort(data, getComparator(order, orderBy, personas));

    return (
        <Box sx={{ width: '100%' }}>
            <Paper sx={{ width: '98%', mb: 2 }}>
                <EnhancedTableToolbar
                    numSelected={selected.length}
                    departamentos={departamentos}
                    eliminarSeleccion={eliminarSeleccion}
                    setEliminarSeleccion={setEliminarSeleccion}
                    tipoColaboradora={tipoColaboradora}
                    tipoEmpresa={tipoEmpresa}
                />
                <TableContainer>
                    <Table sx={{ maxWidth: 2000, minWidth: tipoColaboradora ? 500 : 1300 }}>
                        <EnhancedTableHead
                            numSelected={selected.length}
                            order={order}
                            orderBy={orderBy}
                            onSelectAllClick={handleSelectAllClick}
                            onRequestSort={handleRequestSort}
                            rowCount={data.length}
                            header={header}
                        />
                        <TableBody>
                            {sortedData.map((row, index) => {
                                const isItemSelected = isSelected(row.Id);
                                const labelId = `enhanced-table-checkbox-${index}`;
                                return (
                                    <StyledTableRow
                                        hover
                                        role="checkbox"
                                        aria-checked={isItemSelected}
                                        tabIndex={-1}
                                        key={row.Id}
                                        selected={isItemSelected}
                                    >
                                        <StyledTableCell padding="checkbox">
                                            <Checkbox
                                                color="default"
                                                onClick={(event) => handleClick(event, row.Id)}
                                                checked={isItemSelected}
                                                inputProps={{ 'aria-labelledby': labelId }}
                                            />
                                        </StyledTableCell>

                                        { !tipoEmpresa &&(
                                        <StyledTableCell component="th" id={labelId} scope="row" padding="none">
                                            {row.nombre}
                                        </StyledTableCell> )}

                                        { tipoEmpresa && (
                                            
                                        // Object.entries(row).map(([key, value]) => (
                                        // <StyledTableCell component="th" id={labelId} scope="row" padding="none">
                                        //     {value}
                                        // </StyledTableCell> 
                                        // ))
                                        <>
                                            <StyledTableCell component="th" id={labelId} scope="row" padding="none">
                                                {row.razon_social ? row.razon_social : "-"}
                                            </StyledTableCell>

                                            <StyledTableCell component="th" id={labelId} scope="row" padding="none">
                                                {row.cif ? row.cif : "-"}
                                            </StyledTableCell>

                                            <StyledTableCell component="th" id={labelId} scope="row" padding="none">
                                                {row.direccion ? row.direccion : "-" }
                                            </StyledTableCell>

                                            <StyledTableCell component="th" id={labelId} scope="row" padding="none">
                                                {row.codigo_postal ? row.codigo_postal : "-"}
                                            </StyledTableCell>

                                            <StyledTableCell component="th" id={labelId} scope="row" padding="none">
                                                {row.provincia ? row.provincia : "-"}
                                            </StyledTableCell>

                                            <StyledTableCell component="th" id={labelId} scope="row" padding="none">
                                                {row.poblacion ? row.poblacion : "-"}
                                            </StyledTableCell>                                     
                                        </>

                                        )}

                                        {!tipoColaboradora && !tipoEmpresa && (
                                            <>
                                                <StyledTableCell align="left">{row.Departamentos ? row.Departamentos.filter(dep => dep.tipo === "interno").length : 0}</StyledTableCell>
                                                <StyledTableCell align="left">
                                                    {personas ? personas.filter(persona => persona.Cargos && persona.Cargos.some(cargo => cargo.empresa_id === row.Id)).length : 0}
                                                </StyledTableCell>
                                            </>
                                        )}
                                        <StyledTableCell align="right" sx={{ width: '60px' }}>
                                            <IconosGenerales
                                                tabla="true"
                                                id="edit"
                                                to={tipoColaboradora ? `/gestionpersonas/empresas-colaboradoras/editar/${row.Id}` : tipoEmpresa ? `/gestionpersonas/empresas/editarEmpresa/${row.Id}`: `/gestionpersonas/negocios/editarNegocio/${row.Id}`}
                                            />
                                        </StyledTableCell>
                                    </StyledTableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
            <PaginacionTabla totalPages={meta.totalPages} />
            <SelectNumeroCursos perPage={meta.per_page || 10} />
            <Typography variant="body2" sx={{ textAlign: 'right', fontWeight: 'bold', fontSize: '16px', mt: -3, mr: '2%', color: 'rgba(0, 60, 102, 0.8)' }}>
                Total registros: {meta.total || 0}
            </Typography>
        </Box>
    );
}

EnhancedTable.propTypes = {
    data: PropTypes.array.isRequired,
    header: PropTypes.array.isRequired,
    meta: PropTypes.object.isRequired,
    departamentos: PropTypes.array.isRequired,
    personas: PropTypes.array.isRequired,
    selected: PropTypes.array.isRequired,
    setSelected: PropTypes.func.isRequired,
    eliminarSeleccion: PropTypes.object.isRequired,
    setEliminarSeleccion: PropTypes.func.isRequired,
};

export default EnhancedTable;
